import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Send, Loader2, Pencil, Copy, Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import AdminHeader from "@/components/AdminHeader";

const PAGES = [
  { key: "trusted", label: "People we trust", path: "/people-we-trust", edit: "/admin/website?page=trusted", desc: "Preferred suppliers — security, catering, funfair & more" },
];

export default function AdminSendInfo() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [page, setPage] = useState("trusted");
  const [toEmail, setToEmail] = useState("");
  const [sendingPage, setSendingPage] = useState(false);
  const [reviewLink, setReviewLink] = useState("");
  const [revClientId, setRevClientId] = useState("");
  const [revTo, setRevTo] = useState("");
  const [revEvent, setRevEvent] = useState("");
  const [revMsg, setRevMsg] = useState("");
  const [sendingReview, setSendingReview] = useState(false);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data || [])).catch(() => {});
    api.get("/settings/review-link").then(({ data }) => setReviewLink(data.review_link || "")).catch(() => {});
  }, []);

  const client = clients.find((c) => c.id === clientId);
  const emailOptions = [];
  if (client) {
    if ((client.email || "").trim()) emailOptions.push({ email: client.email.trim(), label: `${client.name} — company email` });
    (client.contacts || []).forEach((ct) => {
      if ((ct.email || "").trim()) emailOptions.push({ email: ct.email.trim(), label: `${ct.name || ct.email}${ct.role ? ` — ${ct.role}` : ""}${ct.is_main ? " (main contact)" : ""}`, main: !!ct.is_main });
    });
    emailOptions.sort((a, b) => (b.main ? 1 : 0) - (a.main ? 1 : 0));
  }

  useEffect(() => {
    if (!client) { setToEmail(""); return; }
    const mainCt = (client.contacts || []).find((x) => x.is_main && (x.email || "").trim());
    const anyCt = (client.contacts || []).find((x) => (x.email || "").trim());
    setToEmail(((mainCt || anyCt || {}).email || client.email || "").trim());
  }, [clientId]);

  const revClient = clients.find((c) => c.id === revClientId);
  const revEmailOptions = [];
  if (revClient) {
    if ((revClient.email || "").trim()) revEmailOptions.push({ email: revClient.email.trim(), label: `${revClient.name} — company email` });
    (revClient.contacts || []).forEach((ct) => {
      if ((ct.email || "").trim()) revEmailOptions.push({ email: ct.email.trim(), label: `${ct.name || ct.email}${ct.role ? ` — ${ct.role}` : ""}` });
    });
  }

  useEffect(() => {
    if (!revClient) { setRevTo(""); return; }
    const mainCt = (revClient.contacts || []).find((x) => (x.is_main || x.role === "Main contact") && (x.email || "").trim());
    const anyCt = (revClient.contacts || []).find((x) => (x.email || "").trim());
    setRevTo(((mainCt || anyCt || {}).email || revClient.email || "").trim());
  }, [revClientId]);

  const send = async () => {
    if (!clientId) { toast.error("Pick a client first"); return; }
    if (!toEmail.trim()) { toast.error("Pick who to send it to"); return; }
    setSendingPage(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/send-offers`, { page, to_email: toEmail.trim() });
      toast.success(data.message);
    } catch (err) { toast.error(formatApiError(err)); }
    setSendingPage(false);
  };

  const saveReviewLink = async () => {
    try {
      const { data } = await api.put("/settings/review-link", { review_link: reviewLink });
      toast.success(data.message);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const sendReview = async () => {
    if (!revClientId) { toast.error("Pick a client first"); return; }
    if (!revTo.trim()) { toast.error("Pick who to send it to"); return; }
    setSendingReview(true);
    try {
      const { data } = await api.post(`/clients/${revClientId}/request-review`, { to_email: revTo.trim(), event_name: revEvent.trim(), message: revMsg.trim() });
      toast.success(data.message);
      setRevEvent("");
      setRevMsg("");
    } catch (err) { toast.error(formatApiError(err)); }
    setSendingReview(false);
  };

  const selCls = "w-full h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]";

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="send-info-page">
      <AdminHeader title="Send client information" testid="send-info-title" backTo="/admin" backTestid="send-info-back" eyebrow="WEMS Portal" />
      <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="send-page-card">
          <h2 className="font-heading text-base font-bold text-[#0F2547]">Send a page to a client</h2>
          <p className="text-xs text-slate-500 mt-0.5">Pick the page, the client, and who gets it — this is the only place these emails go out from.</p>
          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">What to send</label>
              <select value={page} onChange={(e) => setPage(e.target.value)} data-testid="send-page-what" className={selCls}>
                {PAGES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">Client</label>
              <select value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="send-page-client" className={selCls}>
                <option value="">Choose a client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">Send to</label>
              <select value={toEmail} onChange={(e) => setToEmail(e.target.value)} data-testid="send-page-who" disabled={!client} className={`${selCls} disabled:opacity-50`}>
                {emailOptions.length === 0 && <option value="">{client ? "No email on file for this client" : "Pick a client first"}</option>}
                {emailOptions.map((o) => <option key={o.email} value={o.email}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <button onClick={send} disabled={sendingPage || !clientId || !toEmail} data-testid="send-page-send"
            className="mt-4 h-11 px-6 rounded-lg bg-[#00A499] hover:bg-[#008f85] disabled:opacity-50 text-white text-sm font-bold inline-flex items-center gap-2 transition-colors">
            {sendingPage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send it
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="review-request-card">
          <h2 className="font-heading text-base font-bold text-[#0F2547] flex items-center gap-2"><Star className="h-4 w-4 text-[#ED8B00]" /> Request a Google review</h2>
          <p className="text-xs text-slate-500 mt-0.5">After an event, pick the client and send them a friendly review request with your Google link.</p>
          <label className="block mt-4">
            <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">Your Google review link</span>
            <div className="flex gap-2">
              <input value={reviewLink} onChange={(e) => setReviewLink(e.target.value)} placeholder="https://g.page/r/…" data-testid="review-link-input"
                className="flex-1 h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
              <button type="button" onClick={saveReviewLink} data-testid="review-link-save"
                className="h-11 px-4 rounded-lg border border-slate-300 text-slate-600 hover:border-[#005EB8] hover:text-[#005EB8] text-xs font-bold transition-colors">Save</button>
            </div>
          </label>
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">Client</label>
              <select value={revClientId} onChange={(e) => setRevClientId(e.target.value)} data-testid="review-client" className={selCls}>
                <option value="">Choose a client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">Send to</label>
              <select value={revTo} onChange={(e) => setRevTo(e.target.value)} data-testid="review-who" disabled={!revClient} className={`${selCls} disabled:opacity-50`}>
                {revEmailOptions.length === 0 && <option value="">{revClient ? "No email on file for this client" : "Pick a client first"}</option>}
                {revEmailOptions.map((o) => <option key={o.email} value={o.email}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            <input value={revEvent} onChange={(e) => setRevEvent(e.target.value)} placeholder="Which event? (optional)" data-testid="review-event"
              className="h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            <input value={revMsg} onChange={(e) => setRevMsg(e.target.value)} placeholder="Personal line (optional)" data-testid="review-message"
              className="h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
          </div>
          <button onClick={sendReview} disabled={sendingReview || !revClientId || !revTo || !reviewLink.trim()} data-testid="review-send"
            className="mt-4 h-11 px-6 rounded-lg bg-[#ED8B00] hover:bg-[#c97600] disabled:opacity-50 text-white text-sm font-bold inline-flex items-center gap-2 transition-colors">
            {sendingReview ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4" />} Send review request
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="edit-pages-card">
          <h2 className="font-heading text-base font-bold text-[#0F2547]">The pages you're sending</h2>
          <p className="text-xs text-slate-500 mt-0.5">Change what's on each page, or copy the link to paste anywhere yourself.</p>
          <div className="mt-4 space-y-3">
            {PAGES.map((p) => (
              <div key={p.key} className="rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-2" data-testid={`edit-page-row-${p.key}`}>
                <div className="flex-1 min-w-[180px]">
                  <p className="text-sm font-bold text-[#0F2547]">{p.label}</p>
                  <p className="text-xs text-slate-500">{p.desc}</p>
                </div>
                <Link to={p.edit} data-testid={`edit-page-${p.key}`}
                  className="h-9 px-3 rounded-lg bg-[#005EB8] hover:bg-[#004a93] text-white text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
                  <Pencil className="h-3.5 w-3.5" /> Edit this page
                </Link>
                <button type="button" data-testid={`copy-page-${p.key}`}
                  onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}${p.path}`); toast.success("Link copied — paste it anywhere"); }}
                  className="h-9 px-3 rounded-lg border border-slate-300 text-slate-600 hover:border-[#005EB8] hover:text-[#005EB8] text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
                  <Copy className="h-3.5 w-3.5" /> Copy link
                </button>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
